import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { keyframes } from "@emotion/react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FormatColorResetIcon from "@mui/icons-material/FormatColorReset";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import { HIGHLIGHT_COLORS } from "../bible/highlightStorage";

const riseIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(18px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const slipAway = keyframes`
  0% {
    opacity: 1;
    transform: translate(-50%, 0) scale(1);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -36px) scale(0.94);
  }
`;

const pulseCite = keyframes`
  0% { transform: scale(1); }
  40% { transform: scale(1.03); }
  100% { transform: scale(1); }
`;

const DEFAULT_COPY_UI = {
  formats: [
    { id: "reference", label: "出处" },
    { id: "plain", label: "纯文" },
    { id: "quote", label: "金句" },
  ],
  collected: (count) => `已收集 ${count} 节`,
  expandPrev: "包含上一节",
  expandNext: "包含下一节",
  clear: "取消选中",
  copy: "复制经文",
  copied: "已抄入剪贴板",
  copyShort: "复制",
  copiedShort: "已复制",
  share: "分享",
  listen: "从本节朗读",
  listenSelection: "朗读本段",
  listenShort: "朗读",
  note: "写笔记",
  noteShort: "笔记",
  hint: "点另一节扩展选区 · ⌘/Ctrl+C 复制 · Esc 取消",
  region: "已选经文",
  highlight: "高亮划线",
  clearHighlight: "清除高亮",
};

const VerseSelectionDock = ({
  open,
  reference,
  verseCount,
  previewByFormat,
  canExpandStart,
  canExpandEnd,
  onExpandStart,
  onExpandEnd,
  onCopy,
  onShare,
  onPlay,
  onClear,
  onHighlight,
  onTakeNote,
  activeHighlightColor,
  isMobile,
  copyUi = DEFAULT_COPY_UI,
}) => {
  const [format, setFormat] = useState("reference");
  const [copied, setCopied] = useState(false);
  const [slips, setSlips] = useState([]);

  useEffect(() => {
    setCopied(false);
  }, [reference, format]);

  useEffect(() => {
    const ids = (copyUi.formats || []).map((item) => item.id);
    if (ids.length && !ids.includes(format)) {
      setFormat(ids[0]);
    }
  }, [copyUi, format]);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(false), 1800);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const preview = previewByFormat?.[format] || "";
  const previewLines = useMemo(
    () => preview.split("\n").filter((line) => line.trim()),
    [preview]
  );
  const previewLead = previewLines[0] || "";

  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const mobile = isMobile !== undefined ? isMobile : isMobileScreen;

  const isEn = Boolean(copyUi?.copy?.toLowerCase().includes("copy"));
  const copyLabel = mobile
    ? (copied
        ? (copyUi.copiedShort || (isEn ? "Copied" : "已复制"))
        : (copyUi.copyShort || (isEn ? "Copy" : "复制")))
    : (copied
        ? (copyUi.copied || (isEn ? "Copied" : "已抄入剪贴板"))
        : (copyUi.copy || (isEn ? "Copy passage" : "复制经文")));

  const listenLabel = mobile
    ? (copyUi.listenShort || (isEn ? "Play" : "朗读"))
    : (verseCount > 1
        ? (copyUi.listenSelection || (isEn ? "Listen passage" : "朗读本段"))
        : (copyUi.listen || (isEn ? "Listen" : "从本节朗读")));

  const noteLabel = mobile
    ? (copyUi.noteShort || (isEn ? "Note" : "笔记"))
    : (copyUi.note || (isEn ? "Take Note" : "写笔记"));

  const shareLabel = copyUi.share || (isEn ? "Share" : "分享");

  if (!open) return null;

  const handleCopy = async () => {
    try {
      await onCopy(format);
    } catch (error) {
      return;
    }
    setCopied(true);
    const slipId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setSlips((prev) => [...prev, { id: slipId, text: reference }]);
    window.setTimeout(() => {
      setSlips((prev) => prev.filter((item) => item.id !== slipId));
    }, 700);
  };

  const ink = (theme) =>
    theme.palette.mode === "dark" ? "#e2c48a" : "#8b6914";
  const paper = (theme) =>
    theme.palette.mode === "dark"
      ? "rgba(28, 24, 18, 0.92)"
      : "rgba(255, 250, 241, 0.94)";
  const rule = (theme) =>
    theme.palette.mode === "dark"
      ? "linear-gradient(90deg, transparent, #d4b06a 18%, #f0d9a0 50%, #d4b06a 82%, transparent)"
      : "linear-gradient(90deg, transparent, #c4a265 18%, #e8d5a3 50%, #c4a265 82%, transparent)";

  return (
    <Box
      role="region"
      aria-label={copyUi.region}
      aria-live="polite"
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1300,
        pointerEvents: "none",
        px: { xs: 1, sm: 3 },
        pb: {
          xs: "calc(6px + env(safe-area-inset-bottom, 0px))",
          sm: "calc(20px + env(safe-area-inset-bottom, 0px))",
        },
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          pointerEvents: "auto",
          position: "relative",
          width: "100%",
          maxWidth: 640,
          overflow: "hidden",
          borderRadius: { xs: "14px", sm: "24px" },
          bgcolor: paper,
          color: (theme) =>
            theme.palette.mode === "dark" ? "#f6ecd8" : "#2c2114",
          backdropFilter: "blur(18px) saturate(1.2)",
          border: "1px solid",
          borderColor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(226, 196, 138, 0.22)"
              : "rgba(139, 105, 20, 0.16)",
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 18px 50px rgba(0,0,0,0.45)"
              : "0 18px 50px rgba(80, 56, 16, 0.16)",
          animation: `${riseIn} 280ms cubic-bezier(0.22, 1, 0.36, 1)`,
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
          },
        }}
      >
        <Box
          sx={{
            height: 3,
            background: rule,
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: 10,
            left: 14,
            fontFamily: '"Songti SC", "STSong", "Noto Serif SC", serif',
            fontSize: 64,
            lineHeight: 1,
            opacity: 0.08,
            pointerEvents: "none",
            userSelect: "none",
            display: { xs: "none", sm: "block" },
          }}
        >
          “
        </Box>

        <Box sx={{ px: { xs: 1, sm: 2.25 }, pt: { xs: 0.65, sm: 1.5 }, pb: { xs: 0.75, sm: 1.75 } }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 80 }}>
              {canExpandStart && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onExpandStart}
                  startIcon={<KeyboardArrowUpIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    fontSize: { xs: "0.72rem", sm: "0.78rem" },
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    py: 0.3,
                    px: 1,
                    borderRadius: 999,
                    color: ink,
                    borderColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(226, 196, 138, 0.4)"
                        : "rgba(139, 105, 20, 0.3)",
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(226, 196, 138, 0.08)"
                        : "rgba(139, 105, 20, 0.06)",
                    "&:hover": {
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(226, 196, 138, 0.16)"
                          : "rgba(139, 105, 20, 0.12)",
                    },
                  }}
                >
                  {copyUi.expandPrev || "+ 上一节"}
                </Button>
              )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0, textAlign: "center" }}>
              <Typography
                component="div"
                sx={{
                  fontFamily:
                    '"Songti SC", "STSong", "Source Han Serif SC", "Noto Serif SC", serif',
                  fontSize: { xs: "0.95rem", sm: "1.35rem" },
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  lineHeight: 1.25,
                  animation: `${pulseCite} 360ms ease`,
                  "@media (prefers-reduced-motion: reduce)": {
                    animation: "none",
                  },
                }}
              >
                {reference}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: { xs: 0.1, sm: 0.35 },
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: ink,
                  fontWeight: 600,
                  fontSize: { xs: "0.65rem", sm: "0.75rem" },
                }}
              >
                {copyUi.collected(verseCount)}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.75, minWidth: 80 }}>
              {canExpandEnd && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onExpandEnd}
                  endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    fontSize: { xs: "0.72rem", sm: "0.78rem" },
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    py: 0.3,
                    px: 1,
                    borderRadius: 999,
                    color: ink,
                    borderColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(226, 196, 138, 0.4)"
                        : "rgba(139, 105, 20, 0.3)",
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(226, 196, 138, 0.08)"
                        : "rgba(139, 105, 20, 0.06)",
                    "&:hover": {
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(226, 196, 138, 0.16)"
                          : "rgba(139, 105, 20, 0.12)",
                    },
                  }}
                >
                  {copyUi.expandNext || "+ 下一节"}
                </Button>
              )}
              <Tooltip title={copyUi.clear} placement="top">
                <IconButton
                  size="small"
                  onClick={onClear}
                  aria-label={copyUi.clear}
                  sx={{ color: "text.secondary" }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Typography
            data-testid="verse-dock-preview"
            sx={{
              mt: { xs: 0.4, sm: 1.1 },
              px: 0.5,
              fontSize: { xs: "0.78rem", sm: "0.95rem" },
              lineHeight: { xs: 1.4, sm: 1.7 },
              display: isMobile ? "none" : "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              opacity: 0.78,
            }}
          >
            {previewLead}
          </Typography>

          {onHighlight && (
            <Box
              sx={{
                mt: { xs: 0.6, sm: 1.35 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 0.7, sm: 1.4 },
              }}
            >
              {HIGHLIGHT_COLORS.map((item) => {
                const isSelected = activeHighlightColor === item.id;
                return (
                  <Tooltip
                    key={item.id}
                    title={item.labelZh || copyUi.highlight}
                    placement="top"
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onHighlight(item.id);
                      }}
                      sx={{
                        p: 0.5,
                        border: "2px solid",
                        borderColor: isSelected ? item.dotColor : "divider",
                        bgcolor: isSelected
                          ? (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.15)"
                                : "rgba(0,0,0,0.08)"
                          : "transparent",
                        transform: isSelected ? "scale(1.18)" : "scale(1)",
                        "&:hover": {
                          transform: "scale(1.22)",
                          borderColor: item.dotColor,
                        },
                        transition: "transform 150ms ease, border-color 150ms ease, background-color 150ms ease",
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: 14, sm: 20 },
                          height: { xs: 14, sm: 20 },
                          borderRadius: "50%",
                          bgcolor: item.dotColor,
                          boxShadow: isSelected
                            ? `0 0 10px ${item.dotColor}`
                            : `0 2px 6px ${item.dotColor}55`,
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                );
              })}

              <Tooltip title={copyUi.clearHighlight || "清除高亮"} placement="top">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onHighlight(null);
                  }}
                  sx={{
                    color: "text.secondary",
                    p: 0.5,
                    border: "1px dashed",
                    borderColor: "divider",
                    "&:hover": { color: "error.main", borderColor: "error.main" },
                  }}
                >
                  <FormatColorResetIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          <Box
            sx={{
              mt: { xs: 0.5, sm: 1.35 },
              display: "flex",
              justifyContent: "center",
              gap: { xs: 0.4, sm: 0.75 },
            }}
          >
            {copyUi.formats.map((item) => {
              const active = format === item.id;
              return (
                <Button
                  key={item.id}
                  size="small"
                  onClick={() => setFormat(item.id)}
                  aria-pressed={active}
                  sx={{
                    minWidth: 0,
                    px: 1.35,
                    py: 0.25,
                    borderRadius: 999,
                    fontSize: "0.75rem",
                    letterSpacing: "0.08em",
                    color: active ? ink : "text.secondary",
                    bgcolor: (theme) =>
                      active
                        ? theme.palette.mode === "dark"
                          ? "rgba(226, 196, 138, 0.14)"
                          : "rgba(139, 105, 20, 0.1)"
                        : "transparent",
                    border: "1px solid",
                    borderColor: (theme) =>
                      active
                        ? theme.palette.mode === "dark"
                          ? "rgba(226, 196, 138, 0.4)"
                          : "rgba(139, 105, 20, 0.28)"
                        : "transparent",
                    "&:hover": {
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(226, 196, 138, 0.12)"
                          : "rgba(139, 105, 20, 0.08)",
                    },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Box>

          <Box
            sx={{
              mt: { xs: 0.6, sm: 1.25 },
              display: "flex",
              gap: { xs: 0.6, sm: 1 },
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            {onPlay && (
              <Button
                variant="contained"
                color="primary"
                onClick={onPlay}
                startIcon={<PlayArrowIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                sx={{
                  flex: { xs: 1, sm: "none" },
                  minWidth: 0,
                  whiteSpace: "nowrap",
                  py: { xs: 0.8, sm: 0.9 },
                  px: { xs: 0.5, sm: 2 },
                  borderRadius: 999,
                  boxShadow: "none",
                  fontWeight: 700,
                  fontSize: { xs: "0.8rem", sm: "0.9rem" },
                  letterSpacing: "0.04em",
                  height: { xs: 38, sm: 40 },
                  flexShrink: { xs: 1, sm: 0 },
                  "& .MuiButton-startIcon": {
                    mr: { xs: 0.35, sm: 0.75 },
                    ml: { xs: -0.2, sm: -0.5 },
                  },
                  "&:active": {
                    transform: "scale(0.97)",
                  },
                }}
              >
                {listenLabel}
              </Button>
            )}
            <Button
              color="inherit"
              variant="contained"
              onClick={handleCopy}
              startIcon={
                copied ? (
                  <CheckIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                ) : (
                  <ContentCopyIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                )
              }
              sx={{
                flex: 1,
                minWidth: 0,
                whiteSpace: "nowrap",
                py: { xs: 0.8, sm: 0.9 },
                px: { xs: 0.5, sm: 2 },
                borderRadius: 999,
                boxShadow: "none",
                fontWeight: 700,
                fontSize: { xs: "0.8rem", sm: "0.9rem" },
                letterSpacing: { xs: "0.04em", sm: "0.08em" },
                height: { xs: 38, sm: 40 },
                flexShrink: { xs: 1, sm: 0 },
                bgcolor: (theme) =>
                  copied
                    ? theme.palette.mode === "dark"
                      ? "#3d6b45"
                      : "#2f6b3a"
                    : ink(theme),
                color: (theme) =>
                  copied
                    ? "#f4fff6"
                    : theme.palette.mode === "dark"
                      ? "#1c1812"
                      : "#fffaf1",
                "& .MuiButton-startIcon": {
                  mr: { xs: 0.35, sm: 0.75 },
                  ml: { xs: -0.2, sm: -0.5 },
                },
                "&:hover": {
                  boxShadow: "none",
                  bgcolor: (theme) =>
                    copied
                      ? theme.palette.mode === "dark"
                        ? "#3d6b45"
                        : "#2f6b3a"
                      : theme.palette.mode === "dark"
                        ? "#efd19a"
                        : "#6f5310",
                },
                "&:active": {
                  transform: "scale(0.97)",
                },
              }}
            >
              {copyLabel}
            </Button>
            <Button
              variant="outlined"
              onClick={() => onShare(format)}
              startIcon={<WhatsAppIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
              sx={{
                flex: { xs: 1, sm: "none" },
                minWidth: { xs: 0, sm: 100 },
                whiteSpace: "nowrap",
                py: { xs: 0.8, sm: 0.9 },
                px: { xs: 0.5, sm: 2 },
                borderRadius: 999,
                borderColor: "rgba(37, 211, 102, 0.45)",
                color: "#1fa855",
                fontSize: { xs: "0.8rem", sm: "0.9rem" },
                fontWeight: 700,
                height: { xs: 38, sm: 40 },
                flexShrink: { xs: 1, sm: 0 },
                "& .MuiButton-startIcon": {
                  mr: { xs: 0.35, sm: 0.75 },
                  ml: { xs: -0.2, sm: -0.5 },
                },
                "&:hover": {
                  borderColor: "#25D366",
                  bgcolor: "rgba(37, 211, 102, 0.08)",
                },
                "&:active": {
                  transform: "scale(0.97)",
                },
              }}
            >
              {shareLabel}
            </Button>
            {onTakeNote && (
              <Button
                variant="outlined"
                color="primary"
                onClick={onTakeNote}
                startIcon={<NoteAltIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                sx={{
                  flex: { xs: 1, sm: "none" },
                  minWidth: { xs: 0, sm: 96 },
                  whiteSpace: "nowrap",
                  py: { xs: 0.8, sm: 0.9 },
                  px: { xs: 0.5, sm: 2 },
                  borderRadius: 999,
                  fontSize: { xs: "0.8rem", sm: "0.9rem" },
                  fontWeight: 700,
                  height: { xs: 38, sm: 40 },
                  flexShrink: { xs: 1, sm: 0 },
                  "& .MuiButton-startIcon": {
                    mr: { xs: 0.35, sm: 0.75 },
                    ml: { xs: -0.2, sm: -0.5 },
                  },
                  "&:active": {
                    transform: "scale(0.97)",
                  },
                }}
              >
                {noteLabel}
              </Button>
            )}
          </Box>

          {!isMobile && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 1.1,
                textAlign: "center",
                color: "text.secondary",
                letterSpacing: "0.04em",
              }}
            >
              {copyUi.hint}
            </Typography>
          )}
        </Box>

        {slips.map((slip) => (
          <Box
            key={slip.id}
            aria-hidden
            sx={{
              position: "absolute",
              left: "50%",
              top: 18,
              px: 1.5,
              py: 0.4,
              borderRadius: 999,
              bgcolor: paper,
              border: "1px solid",
              borderColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(226, 196, 138, 0.35)"
                  : "rgba(139, 105, 20, 0.25)",
              fontFamily:
                '"Songti SC", "STSong", "Noto Serif SC", serif',
              fontSize: "0.85rem",
              letterSpacing: "0.06em",
              whiteSpace: "nowrap",
              pointerEvents: "none",
              animation: `${slipAway} 680ms ease forwards`,
              "@media (prefers-reduced-motion: reduce)": {
                display: "none",
              },
            }}
          >
            {slip.text}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default VerseSelectionDock;
